import { router } from "@inertiajs/react"
import { FormEvent, useState } from "react"
import { MailPlus } from "lucide-react"

type City = {
  id: number
  name: string
  slug: string
}

type Props = {
  cities: City[]
}

export default function Dispatch({ cities }: Props) {
  const [email, setEmail] = useState("")
  const [citySlug, setCitySlug] = useState("")
  const [formError, setFormError] = useState("")

  const handleSubscribe = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!citySlug) {
      setFormError("Select city edition before subscribing.")
      return
    }

    setFormError("")
    router.post(`/cities/${citySlug}/subscriptions`, { email })
  }

  return (
    <main className="ch-noise relative z-10 flex-grow">
      <section className="ch-container py-12 md:py-20">
        <div className="mx-auto grid max-w-4xl grid-cols-1 gap-8">
          <div className="text-center">
            <h1 className="ch-masthead mb-4 text-[hsl(var(--ch-primary))]">The Municipal Dispatch</h1>
            <p className="ch-body-lg mx-auto max-w-2xl text-[hsl(var(--ch-muted))]">
              Get concise city-specific council updates delivered to your inbox. Choose your city edition and stay current
              on local decisions that affect housing, transit, and taxes.
            </p>
          </div>

          <div className="border-2 border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-surface))] p-8">
            <div className="mb-6 text-center">
              <MailPlus className="mx-auto mb-3 h-9 w-9 text-[hsl(var(--ch-primary))]" />
              <h2 className="ch-headline-md text-[hsl(var(--ch-primary))]">Subscribe to your city edition</h2>
            </div>

            <form className="space-y-4" onSubmit={handleSubscribe}>
              <div>
                <label className="ch-label-caps mb-1 block text-[hsl(var(--ch-primary))]" htmlFor="dispatch-city-edition">
                  Select city edition
                </label>
                <select
                  className="w-full border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-bg))] p-3 text-sm text-[hsl(var(--ch-primary))] focus:outline-none"
                  id="dispatch-city-edition"
                  onChange={(event) => setCitySlug(event.target.value)}
                  required
                  value={citySlug}
                >
                  <option value="">Choose a city...</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.slug}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="ch-label-caps mb-1 block text-[hsl(var(--ch-primary))]" htmlFor="dispatch-email">
                  Electronic Address
                </label>
                <input
                  className="w-full border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-bg))] p-3 text-sm text-[hsl(var(--ch-primary))] placeholder:text-[hsl(var(--ch-muted))] focus:outline-none"
                  id="dispatch-email"
                  name="email"
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="citizen@example.com"
                  required
                  type="email"
                  value={email}
                />
              </div>

              {formError ? <p className="text-sm text-rose-700">{formError}</p> : null}

              <button
                className="ch-label-caps w-full border-2 border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-primary))] py-4 text-white transition-colors hover:bg-[hsl(var(--ch-bg))] hover:text-[hsl(var(--ch-primary))]"
                type="submit"
              >
                Receive Dispatch
              </button>
            </form>
          </div>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <blockquote className="border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-surface-low))] p-6">
              <p className="mb-4 text-sm leading-relaxed text-[hsl(var(--ch-muted))]">
                “The Vancouver dispatch helps me track council votes without digging through three-hour videos. The
                summaries are clear and usually arrive before I start work.”
              </p>
              <footer className="ch-label-caps text-[hsl(var(--ch-primary))]">Maya K., Mount Pleasant resident</footer>
            </blockquote>

            <blockquote className="border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-surface-low))] p-6">
              <p className="mb-4 text-sm leading-relaxed text-[hsl(var(--ch-muted))]">
                “I subscribed for zoning updates and stayed for the budget notes. It is one of the few local newsletters
                that feels factual and actually useful.”
              </p>
              <footer className="ch-label-caps text-[hsl(var(--ch-primary))]">Daniel R., Kitsilano homeowner</footer>
            </blockquote>
          </section>
        </div>
      </section>
    </main>
  )
}
